import { pick } from "@/lib/format";
import { BarList, ColumnChart, Empty, LanguagePie, Panel } from "./Charts";

const STAGES = ["visit", "browse", "dish", "continue"];

export function Funnel({ funnel, t }) {
  const top = funnel[0] || 0;
  return (
    <Panel title={t("an.funnel")} hint={t("an.funnelHint")} testid="analytics-funnel">
      {!top ? <Empty t={t} /> : (
        <div className="grid gap-2 sm:grid-cols-4">
          {STAGES.map((k, i) => {
            const n = funnel[i];
            const drop = i && funnel[i - 1] ? Math.round(100 - (100 * n) / funnel[i - 1]) : null;
            return (
              <div key={k} className="relative overflow-hidden rounded-xl border bg-secondary/30 p-3" data-testid={`an-funnel-${k}`}>
                <div className="absolute inset-y-0 start-0 bg-primary/15" style={{ width: `${(100 * n) / top}%` }} />
                <div className="relative space-y-1">
                  <div className="text-xs text-muted-foreground">{i + 1}. {t(`an.f.${k}`)}</div>
                  <div className="text-2xl font-extrabold tabular-nums">{n}</div>
                  <div className="text-[11px] text-muted-foreground">{drop === null ? t("an.allSessions") : t("an.dropOff", { n: drop })}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Panel>
  );
}

export function Behavior({ data, t, lang, catName }) {
  const item = (i) => pick(i, "title", lang).text;
  const quick = data.items.filter((i) => i.quick_closes > 0).sort((a, b) => b.quick_close_rate - a.quick_close_rate || b.quick_closes - a.quick_closes).slice(0, 8);
  const longest = [...data.items].filter((i) => i.avg_view_s > 0).sort((a, b) => b.avg_view_s - a.avg_view_s).slice(0, 8);
  const sec = (v) => t("an.seconds", { n: v });
  const L = data.languages;
  const days = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(lang === "ar" ? "ar-SA-u-nu-latn" : "en-GB", { weekday: "short" }));
  const qr = data.summary.qr_visits;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {data.menus?.length > 0 && (
        <Panel title={t("an.menus")} hint={t("an.menusHint")} testid="an-menus">
          <BarList rows={data.menus.map((m) => ({ key: m.id, name: `${pick(m, "name", lang).text} · ${t("an.menuViews", { n: m.dish_views })}`, value: m.selections }))} />
        </Panel>
      )}
      <Panel title={t("an.mostViewed")} testid="an-most-viewed">
        {data.items.some((i) => i.views) ? <BarList rows={data.items.filter((i) => i.views).slice(0, 8).map((i) => ({ key: i.id, name: item(i), value: i.views }))} /> : <Empty t={t} />}
      </Panel>
      <Panel title={t("an.popularCats")} hint={t("an.popularCatsHint")} testid="an-popular-cats">
        {data.categories.length ? <BarList rows={data.categories.map((c) => ({ key: c.id, name: catName(c.id), value: c.item_views + c.clicks }))} /> : <Empty t={t} />}
      </Panel>
      <Panel title={t("an.engagement")} hint={t("an.engagementHint")} testid="an-engagement">
        {longest.length ? <BarList rows={longest.map((i) => ({ key: i.id, name: item(i), value: i.avg_view_s }))} format={sec} /> : <Empty t={t} />}
      </Panel>
      <Panel title={t("an.quick")} hint={t("an.quickHint")} testid="an-quick-close">
        {quick.length ? <BarList rows={quick.map((i) => ({ key: i.id, name: `${item(i)} (${i.quick_closes})`, value: i.quick_close_rate }))} format={(v) => `${v}%`} /> : <Empty t={t} />}
      </Panel>
      <Panel title={t("an.attention")} hint={t("an.attentionHint")} testid="an-attention">
        {data.categories.some((c) => c.attention_s) ? <BarList rows={[...data.categories].sort((a, b) => b.attention_s - a.attention_s).map((c) => ({ key: c.id, name: catName(c.id), value: c.attention_s }))} format={sec} /> : <Empty t={t} />}
      </Panel>
      <Panel title={t("an.clicks")} testid="an-clicks">
        {data.clicks.length ? <BarList rows={data.clicks.map((c) => ({ key: c.zone, name: t(`an.zone.${c.zone}`), value: c.count }))} /> : <Empty t={t} />}
      </Panel>
      <Panel title={t("an.scroll")} hint={t("an.scrollHint")} testid="an-scroll">
        {data.summary.sessions ? <ColumnChart data={data.scroll.map((s) => ({ name: `${s.depth}%`, value: s.pct }))} label={t("an.reachPct")} /> : <Empty t={t} />}
        {data.categories.length > 0 && <BarList rows={data.categories.map((c) => ({ key: c.id, name: catName(c.id), value: c.reach_pct }))} format={(v) => `${v}%`} testid="an-category-reach" />}
      </Panel>
      <Panel title={t("an.languages")} testid="an-languages">
        {(L.sessions.en || 0) + (L.sessions.ar || 0) ? <LanguagePie en={L.sessions.en || 0} ar={L.sessions.ar || 0} t={t} /> : <Empty t={t} />}
        <p className="text-xs text-muted-foreground">{t("an.langDetail", { en: L.dish_views.en || 0, ar: L.dish_views.ar || 0, s: (L.switches.en || 0) + (L.switches.ar || 0) })}</p>
      </Panel>
      <Panel title={t("an.byHour")} testid="an-by-hour">
        <ColumnChart data={data.hourly.map((v, h) => ({ name: String(h), value: v }))} label={t("an.m.visits")} />
      </Panel>
      <Panel title={t("an.byDay")} testid="an-by-day">
        <ColumnChart data={data.weekday.map((v, i) => ({ name: days[i], value: v }))} label={t("an.m.visits")} />
      </Panel>
      <Panel title={t("an.searches")} testid="an-searches">
        {data.searches.length ? <BarList rows={data.searches.slice(0, 10).map((s) => ({ key: s.q, name: s.q, value: s.count }))} /> : <Empty t={t} />}
        {data.no_result_searches.length > 0 && (
          <div className="space-y-1" data-testid="an-no-results">
            <div className="text-xs font-semibold text-amber-300">{t("an.noResultSearches")}</div>
            <div className="flex flex-wrap gap-1.5">{data.no_result_searches.map((s) => <span key={s.q} className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-200">{s.q} ({s.count})</span>)}</div>
          </div>
        )}
      </Panel>
      <Panel title={t("an.qr")} hint={t("an.qrHint")} testid="an-qr">
        <div className="text-3xl font-extrabold tabular-nums" data-testid="an-qr-visits">{qr}</div>
        <p className="text-xs text-muted-foreground">{t("an.qrShare", { n: data.summary.visits ? Math.round((100 * qr) / data.summary.visits) : 0 })}</p>
      </Panel>
    </div>
  );
}
